/** Publishing Credential Vault (M10 Phase 3). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  Check, ExternalLink, Facebook, Instagram, Linkedin, Loader2, Music2,
  Play, RefreshCw, ShieldCheck, Trash2, Youtube,
} from "lucide-react";

import { useCurrentWorkspace } from "@/entities/workspace";
import { WorkflowStages } from "@/widgets/WorkflowStages/WorkflowStages";
import {
  disconnect, listConnections, sandboxConnect, startOAuth, testConnection,
  type ConnectionStatus, type PublishingConnection,
} from "@/entities/publishing";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Skeleton } from "@/shared/ui/skeleton";
import { PremiumEmpty } from "@/shared/ui/premium-empty";
import { Radio } from "lucide-react";

const STATUS_TONE: Record<ConnectionStatus, string> = {
  disconnected: "bg-zinc-200 text-zinc-800 dark:bg-zinc-900 dark:text-zinc-200",
  pending_oauth: "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200",
  connected: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200",
  sandbox: "bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-200",
  expired: "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200",
  error: "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200",
};

const CHANNEL_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
  youtube: Youtube, tiktok: Music2,
  threads: Play, x: Play, pinterest: Play,
};

export default function PublishingPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  if (!workspace) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <PremiumEmpty
          testId="publishing-empty"
          icon={<Radio className="h-5 w-5" />}
          eyebrow="Publishing"
          title="Connect a channel to bring the OS to life."
          body="Every scheduled asset ships through one secure vault. Once a platform is connected, campaigns publish themselves — no re-authentication per send."
          bullets={[
            "OAuth-encrypted at rest, scoped per workspace",
            "Durable queue with retries + dead-letter surfacing",
            "One-click cancel and manual retry, always workspace-locked",
          ]}
          primary={{ label: "Open a workspace", to: "/", testId: "publishing-empty-open" }}
          secondary={{ label: "Read the guide", to: "/academy" }}
        />
      </div>
    );
  }
  return (
    <div data-testid="publishing-page" className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <WorkflowStages current="publishing" />
      <header>
        <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          <ShieldCheck className="h-3 w-3" /> M10 Phase 3 · OAuth-ready adapters
        </div>
        <h1 data-testid="publishing-title" className="mt-3 ds-headline ds-headline--display">
          Publishing connections
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Connect a channel once — every future scheduled asset routes through the same secure vault.
          Sandbox connections keep the pipeline flowing while you wait on platform approvals.
        </p>
      </header>
      <Vault wsId={workspace.id} />
    </div>
  );
}

function Vault({ wsId }: { wsId: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["publishing", wsId], queryFn: () => listConnections(wsId) });

  const refresh = () => qc.invalidateQueries({ queryKey: ["publishing", wsId] });

  const oauth = useMutation({
    mutationFn: (channel: string) => startOAuth(wsId, {
      channel, redirect_uri: `${window.location.origin}/publishing`,
    }),
    onSuccess: (r) => {
      // Preview: no live client_id — surface the URL for the operator.
      toast.info(`OAuth ready — open ${r.authorize_url} once client_id is provisioned.`);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const sandbox = useMutation({
    mutationFn: ({ channel, label }: { channel: string; label: string }) =>
      sandboxConnect(wsId, { channel, account_label: label }),
    onSuccess: () => { toast.success("Sandbox connection saved."); refresh(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const test = useMutation({
    mutationFn: (channel: string) => testConnection(wsId, channel),
    onSuccess: (r) => { toast.success(`Test: ${r.result}`); refresh(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const rm = useMutation({
    mutationFn: (channel: string) => disconnect(wsId, channel),
    onSuccess: () => { toast.success("Disconnected."); refresh(); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (q.isLoading) {
    return <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-56 w-full" />)}
    </div>;
  }
  const conns = q.data ?? [];
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {conns.map((c) => (
        <ConnectionCard key={c.channel} conn={c}
                        onOAuth={() => oauth.mutate(c.channel)}
                        onSandbox={(label) => sandbox.mutate({ channel: c.channel, label })}
                        onTest={() => test.mutate(c.channel)}
                        onDisconnect={() => rm.mutate(c.channel)}
                        busy={oauth.isPending || sandbox.isPending || test.isPending || rm.isPending} />
      ))}
    </div>
  );
}

function ConnectionCard({
  conn, onOAuth, onSandbox, onTest, onDisconnect, busy,
}: {
  conn: PublishingConnection;
  onOAuth: () => void;
  onSandbox: (label: string) => void;
  onTest: () => void;
  onDisconnect: () => void;
  busy: boolean;
}): JSX.Element {
  const Icon = CHANNEL_ICON[conn.channel] ?? Play;
  const [sandboxLabel, setSandboxLabel] = useState(conn.account_label ?? `@${conn.channel}`);
  const [showSandbox, setShowSandbox] = useState(false);
  return (
    <Card data-testid={`connection-card-${conn.channel}`} className="border-border/60 bg-card/70">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-indigo-500" />
          {conn.display_name}
        </CardTitle>
        <CardDescription>{conn.profile?.scope_description ?? "OAuth-ready adapter."}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        <div className="flex items-center justify-between">
          <Badge data-testid={`connection-status-${conn.channel}`}
                  className={`${STATUS_TONE[conn.status]} font-mono text-[10px] uppercase`}>
            {conn.status.replace("_", " ")}
          </Badge>
          {conn.account_label ? (
            <span className="font-mono text-muted-foreground">{conn.account_label}</span>
          ) : null}
        </div>
        {conn.last_test_result ? (
          <p className="text-muted-foreground">Last test: {conn.last_test_result}</p>
        ) : null}
        {conn.last_error ? (
          <p className="text-rose-500" data-testid={`connection-error-${conn.channel}`}>{conn.last_error}</p>
        ) : null}
        <div className="flex flex-wrap gap-1">
          {(conn.profile?.scopes ?? []).slice(0, 3).map((s) => (
            <span key={s} className="rounded-full border border-border/40 px-1.5 py-0.5 text-[9px] font-mono text-muted-foreground">{s}</span>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1 pt-1">
          <Button size="sm" variant="outline" onClick={onOAuth}
                  data-testid={`connection-oauth-${conn.channel}`} disabled={busy}>
            Connect via OAuth
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowSandbox((v) => !v)}
                  data-testid={`connection-sandbox-toggle-${conn.channel}`} disabled={busy}>
            Sandbox
          </Button>
          {conn.status !== "disconnected" ? (
            <>
              <Button size="sm" variant="ghost" onClick={onTest}
                      data-testid={`connection-test-${conn.channel}`} disabled={busy}>
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
                Test
              </Button>
              <Button size="sm" variant="ghost" onClick={onDisconnect}
                      data-testid={`connection-disconnect-${conn.channel}`} disabled={busy}>
                <Trash2 className="mr-1 h-3 w-3" /> Disconnect
              </Button>
            </>
          ) : null}
        </div>
        {showSandbox ? (
          <div className="rounded-md border border-sky-500/40 bg-sky-50/50 p-2 dark:bg-sky-950/20">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Sandbox account label
            </Label>
            <div className="mt-1 flex gap-1">
              <Input value={sandboxLabel} onChange={(e) => setSandboxLabel(e.target.value)}
                     data-testid={`connection-sandbox-input-${conn.channel}`} className="h-7 text-xs" />
              <Button size="sm" onClick={() => onSandbox(sandboxLabel)} disabled={busy}
                      data-testid={`connection-sandbox-save-${conn.channel}`}>
                <Check className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ) : null}
        {conn.profile?.docs_url ? (
          <a href={conn.profile.docs_url} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground">
            Platform docs <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </CardContent>
    </Card>
  );
}
